import { useCallback, useState, useEffect, useMemo, SetStateAction, Dispatch } from "react";
import classNames from 'classnames';
import { useParams } from "react-router";

import { Survey, PageModule, SurveyQuestion, SurveyQuestionOption, SurveyAnswerBody } from "../../../../../types/working-model";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import SurveyOption from "./survey-option";
import { getStylingOverrides } from "../../../utils";
import { ParamsProps } from "../../../live-event";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { SubmitSurveyAnswer } from "../../../../../connection/save-survey-answer";
import { GetSurveyQuestionResults } from "../../../../../connection/surveys";
import { getStorageObject, setStorageObject } from "../../../../../utils/local-storage";
import { useTranslate } from "../../../../../i18n/useTranslationModules";

import { getPollAnswerLocalStorageKey, getPollAnswerLocalStorageKeyBackwardsCompatible } from "./survey";
import useTranslationRoute from "../../../hooks/use-translation-route";
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";

interface ICheckboxSurveyQuestionProps {
	question: SurveyQuestion;
	module: PageModule;
	template: string;
	sessionUuid?: string;
	survey: Survey;
	setCurrentQuestion: Dispatch<SetStateAction<number>>;
	currentQuestion: number;
	maxQuestions: number;
	viewAll: boolean;
}

const CheckboxSurveyQuestion: React.FC<ICheckboxSurveyQuestionProps> = ({
	question,
	module,
	template,
	sessionUuid,
	survey,
	setCurrentQuestion,
	currentQuestion,
	maxQuestions,
	viewAll
}) => {
	const { show_results, survey: surveyId } = survey;
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		primaryButtonOverrides,
	} = stylingOverrides;

	const { language }: ParamsProps = useParams();

	const [selected, setSelected] = useState<number[]>([]);
	const [percentages, setPercentages] = useState<{ [key: number]: number }>({});
	const [totalAnswers, setTotalAnswers] = useState(0);
	const [isAnswered, setIsAnswered] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const eventId = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);
	const sessions = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.sessions);
	const channel_id = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.channel);
	const registrationId = useTypedSelector((state) => state.LiveEventReducer.registrationId);
	const registration_on = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.registration_on);
	const workingSession = useTypedSelector((state) => state.CreateEventReducer.workingSession);
	const previewMode = useTypedSelector((state) => state.CreateEventReducer.previewMode);
	const token = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const leaderboardEnabled = !!useTypedSelector(state => state.LiveEventReducer.leaderboardIsOn);

	const session = useMemo(() => {
		if (previewMode === 'session') return workingSession;

		return sessions?.find(session => session.uuid === sessionUuid);
	}, [previewMode, sessions, sessionUuid, workingSession]);

	const namespace = useTranslationRoute();
	const { t } = useTranslate([namespace, "session"]);
	const isSubmitDisabled = isAnswered || !selected.length || (previewMode === 'session') || isSubmitting;

	// Get question stats (answer submission totals for each option)
	const getStatsResults = useCallback(async (selectedOptions?: string[]) => {
		try {
			const level = session ? 'session' : 'event';
			const levelId = session ? session.session : eventId;
			if (!levelId) return;

			const stats = await GetSurveyQuestionResults(level, levelId, question.survey_question, question.question_type);

			// Since stats results are cached, this allows a fresh answer to be counted
			if (selectedOptions) {
				stats.total += selectedOptions.length;
				selectedOptions.forEach((option) => stats.counts[option] += 1);
			}
			setPercentages(stats.counts);
			setTotalAnswers(stats.total);
		} catch (e) {
			console.error(e);
		}
	}, [eventId, question, session]);

	useEffect(() => {
		if (!eventId) return;

		// if the user refreshes we want to keep the answers they submitted shown
		const lsKey = getPollAnswerLocalStorageKey(question, surveyId, eventId);
		const lsKeyBackwardsCompatible = getPollAnswerLocalStorageKeyBackwardsCompatible(question, surveyId);
		const answer: SurveyAnswerBody | null = getStorageObject(lsKey) ?? getStorageObject(lsKeyBackwardsCompatible);

		// If question has been answered, show answer selected, disable submit and get stats
		if (answer?.checkbox_answer) {
			setSelected(answer.checkbox_answer.map(Number));
			setIsAnswered(true);
			if (show_results) {
				getStatsResults();
			}
		}
	}, [eventId, getStatsResults, question, surveyId, show_results]);

	const showNext = () => {
		return <button disabled={currentQuestion + 1 === maxQuestions} onClick={() => setCurrentQuestion(currentQuestion + 1)}>{t("session:Next")}</button>;
	};

	const showSubmit = () => {
		return (
			<button
				className={classNames('primary next', primaryButtonOverrides)} type="submit"
				onClick={handleSubmit}
				disabled={isSubmitDisabled}
			>
				{isSubmitting ? (
					<WaitingIndicator />
				) : t(`${namespace}:callToAction`, (content?.callToAction?.button_name?.[language] || t("session:Submit")))
				}
			</button>
		);
	};

	const displayButtonRow = () => {
		return (
			<div className='survey-pagination'>
				{!viewAll && <button disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>{t("session:Previous")}</button>}
				<span>{currentQuestion + 1}/{maxQuestions}</span>
				{viewAll || (isSubmitDisabled && (Object.keys(selected).length === 0)) || (!isSubmitDisabled && (Object.keys(selected).length !== 0)) ? (
					<>
						{showSubmit()}
					</>
				) : (
					<>
						{showNext()}
					</>
				)}
			</div >
		);
	};

	const handleChange = (value: string | number) => {
		// handles selecting an option
		const optionId = Number(value);
		setSelected(prev => {
			const isChecked = prev.includes(optionId);
			if (isChecked) {
				return prev.filter((id) => id !== optionId);
			} else {
				return [...prev, optionId];
			}
		});
	};

	// Calculates percentage for option results/stats bar
	const percentage = (option: SurveyQuestionOption) => {
		if (!totalAnswers) {
			return null;
		}
		if (percentages[option.survey_question_option]) {
			return Math.round(percentages[option.survey_question_option] / totalAnswers * 100);
		}

		return 0;
	};

	const renderOptions = () => {
		return question.options.map(option => (
			<div
				className="survey-option-wrapper"
				key={`survey-option-${option.survey_question_option}`}
			>
				<SurveyOption
					value={option.survey_question_option}
					title={t(`${namespace}:survey.${surveyId}.questions.${question?.survey_question}.options.${option.survey_question_option}`, option.label?.[language] as string ?? option.label.base)}
					selected={selected.includes(option.survey_question_option)}
					onClick={handleChange}
					percentage={show_results ? percentage(option) : null}
					disabled={isAnswered}
				/>
			</div>
		));
	};

	// Submits answer to question
	const handleSubmit = async () => {
		// this function handles the submission of options selected
		if (question.required && !selected.length) return;
		if (!module.id || !eventId) return;

		const checkboxAnswer = selected.map((id) => id.toString());
		const body: SurveyAnswerBody = {
			session: session?.session,
			survey_question: question.survey_question,
			module: module.id,
			event: eventId,
			text_answer: null,
			radio_answer: null,
			checkbox_answer: checkboxAnswer,
			session_uuid: session?.uuid,
			language: language,
			channel: channel_id,
			leaderboard_enabled: leaderboardEnabled
		};
		if (registration_on && registrationId) {
			body.registration_id = registrationId;
		}

		try {
			setIsSubmitting(true);

			// Get stats first; the fresh answer will be tacked on simultaneously (prevents possible double count)
			if (show_results) {
				await getStatsResults(checkboxAnswer);
			}

			// Send the message to microservices
			await SubmitSurveyAnswer(body, token);

			//set in local storage in case they refresh
			const lsKey = getPollAnswerLocalStorageKey(question, surveyId, eventId);
			setStorageObject(lsKey, body, 48);
			setIsAnswered(true);
		} catch (err) {
			setPercentages({});
			setTotalAnswers(0);
			console.error(err);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={classNames('question-container')}>
			<TypographyItem className={classNames('question-title evt-heading-4', template)} tagName="p">
				{t(`${namespace}:survey.${surveyId}.questions.${question?.survey_question}`, (question?.title?.[language] as string ?? question?.title?.base))}
			</TypographyItem>
			<div>{renderOptions()}</div>
			{displayButtonRow()}
		</div >
	);
};

export default CheckboxSurveyQuestion;
